import { useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useQuery } from '@tanstack/react-query';
import {
  InboxNotificationSchema,
  UnreadCountSchema,
  type InboxNotificationDto,
  type MeResponse,
} from '@taqon/contracts';
import { useNavigate } from 'react-router';
import { getData, getPage } from '../../shared/api/client';
import { INBOX, INBOX_KEY, relativeTime, subjectPath, useInboxActions } from './inbox';

const LATEST = 8;

/**
 * The header bell (FRD-CORE-025): the unread count, refreshed every minute and when the window
 * regains focus, and the latest notifications. Choosing one marks it read and opens its record
 * when there is a page for it.
 */
export function NotificationBell({ me }: { me: MeResponse }) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { markRead } = useInboxActions(me.csrfToken);

  const unread = useQuery({
    queryKey: [...INBOX_KEY, 'unread-count'],
    queryFn: () => getData(`${INBOX}/unread-count`, UnreadCountSchema),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const latest = useQuery({
    queryKey: [...INBOX_KEY, 'latest'],
    enabled: anchor !== null,
    queryFn: () => getPage(`${INBOX}?pageSize=${String(LATEST)}`, InboxNotificationSchema),
  });
  const count = unread.data?.unread ?? 0;
  const close = () => {
    setAnchor(null);
  };
  const open = async (notification: InboxNotificationDto) => {
    close();
    if (notification.deliveryStatus === 'sent') {
      try {
        await markRead.mutateAsync(notification.id);
      } catch {
        // Opening the record matters more than the read mark; the bell shows it still unread.
      }
    }
    const path = subjectPath(me, notification);
    if (path !== null) await navigate(path);
  };

  const label = count === 0 ? 'Notifications' : `Notifications, ${String(count)} unread`;
  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          onClick={(event) => {
            setAnchor(event.currentTarget);
          }}
        >
          <Badge badgeContent={count} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" component="p">
            Notifications
          </Typography>
        </Box>
        <Divider />
        {latest.isPending && (
          <MenuItem disabled>
            <ListItemText primary="Loading notifications…" />
          </MenuItem>
        )}
        {latest.isError && (
          <MenuItem disabled>
            <ListItemText primary={latest.error.message} />
          </MenuItem>
        )}
        {latest.data?.items.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No notifications yet" />
          </MenuItem>
        )}
        {latest.data?.items.map((notification) => (
          <MenuItem
            key={notification.id}
            onClick={() => {
              void open(notification);
            }}
            sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}
          >
            <ListItemText
              primary={notification.title}
              secondary={`${notification.subjectLabel} · ${relativeTime(notification.createdAt)}`}
              slotProps={{
                primary: {
                  sx: { fontWeight: notification.deliveryStatus === 'sent' ? 700 : 400 },
                },
              }}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            close();
            void navigate('/notifications');
          }}
        >
          <ListItemText primary="All notifications" slotProps={{ primary: { color: 'primary' } }} />
        </MenuItem>
      </Menu>
    </>
  );
}
